import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/* Setup + cleanup for per-instructor demo students. Lives in the Manage tab.
   Create (capped), name, pick course enrollments, delete, and purge activity. */
export default function TestStudentsSection() {
  const { apiFetch } = useAuth();
  const [students, setStudents] = useState(null);
  const [courses, setCourses]   = useState([]);
  const [flash, setFlash]       = useState('');
  const [busy, setBusy]         = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        apiFetch('/api/courses/test-students'),
        apiFetch('/api/courses/mine'),
      ]);
      if (sRes.ok) setStudents(await sRes.json());
      else setStudents([]);
      if (cRes.ok) setCourses(await cRes.json());
    } catch { setStudents([]); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const flashMsg = (m) => { setFlash(m); setTimeout(() => setFlash(''), 3000); };

  const createStudent = async () => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/courses/test-students', {
        method: 'POST', body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { flashMsg(d.message || 'Created.'); load(); }
      else flashMsg(d.detail || 'Could not create.');
    } finally { setBusy(false); }
  };

  const deleteStudent = async (id) => {
    const res = await apiFetch(`/api/courses/test-students/${id}`, { method: 'DELETE' });
    if (res.ok) { flashMsg('Deleted.'); load(); }
  };

  const resetStudent = async (id) => {
    const res = await apiFetch(`/api/courses/test-students/${id}/reset`, { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) flashMsg(d.message || 'Cleared.');
  };

  const clearAll = async () => {
    const res = await apiFetch('/api/courses/test-students/clear-all', { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) flashMsg(d.message || 'Cleared all.');
  };

  const toggleEnrollment = async (student, courseId) => {
    const current = new Set(student.courses.map(c => c.id));
    if (current.has(courseId)) current.delete(courseId);
    else current.add(courseId);
    const res = await apiFetch(`/api/courses/test-students/${student.id}/enrollments`, {
      method: 'PUT', body: JSON.stringify({ course_ids: Array.from(current) }),
    });
    if (res.ok) load();
  };

  const atCap = students && students.length >= 3;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Test Students</h3>
      <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 10, lineHeight: 1.5 }}>
        Demo students only you can see. Create up to 3, enroll them in your courses, then
        add them to a live queue from the demo panel on the queue screen to see how things flow.
        They're excluded from your analytics.
      </p>
      {flash && <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>{flash}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button className="btn-primary" onClick={createStudent} disabled={busy || atCap}
          style={{ fontSize: 13 }} title={atCap ? 'You have the maximum of 3' : ''}>
          + Create test student
        </button>
        {students && students.length > 0 && (
          <button className="btn-ghost" onClick={clearAll} style={{ fontSize: 13 }}
            title="Clear queue activity for all test students (keeps the students)">
            Clear all demo activity
          </button>
        )}
      </div>

      {students === null ? (
        <div style={{ fontSize: 13, color: 'var(--txt-faint)' }}>Loading…</div>
      ) : students.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--txt-dim)' }}>
          No test students yet. Create one to start demoing.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {students.map(s => {
            const enrolledIds = new Set(s.courses.map(c => c.id));
            return (
              <div key={s.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>{s.display_name}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" onClick={() => resetStudent(s.id)}
                      style={{ fontSize: 11, padding: '3px 8px' }}
                      title="Clear this student's queue activity, keep the account">
                      Reset activity
                    </button>
                    <button className="btn-ghost" onClick={() => deleteStudent(s.id)}
                      style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red, #e5484d)' }}>
                      Delete
                    </button>
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--txt-faint)', marginBottom: 6 }}>Enrolled in:</div>
                {courses.length === 0 ? (
                  <span style={{ fontSize: 12, color: 'var(--txt-faint)' }}>No courses to enroll in.</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {courses.map(c => {
                      const on = enrolledIds.has(c.id);
                      return (
                        <button key={c.id} onClick={() => toggleEnrollment(s, c.id)}
                          className={on ? 'btn-primary' : 'btn-ghost'}
                          style={{ fontSize: 11, padding: '4px 8px' }}>
                          {on ? '✓ ' : ''}{c.course_code}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
